class CartDrawerFreeShipping {
  constructor() {
    this.notifications = [];
    this.freeShippingThreshold = 5900; // Default
    this.setupComplete = false;

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Re-query DOM elements in case they were replaced
    this.requeryElements();

    if (this.notifications.length === 0) {
      // Retry after a short delay if element not found
      setTimeout(() => {
        // One more check to prevent infinite loop if elements truly don't exist
        if (!this.setupComplete) this.requeryElements();
        // If still empty but we wanted to setup, maybe just mark complete so we don't block?
        // But let's try to setup listeners anyway just in case elements appear later via AJAX
      }, 100);
    }

    // Attempt to get threshold from the first found element
    if (this.notifications.length > 0) {
      const thresholdAttr = this.notifications[0].getAttribute('data-free-shipping-threshold');
      this.freeShippingThreshold = thresholdAttr ? parseInt(thresholdAttr, 10) : 5900;
    }

    // Only set up event listeners once
    if (!this.setupComplete) {
      // Listen to cart update events from MinimogEvents
      if (window.MinimogEvents && window.MinimogTheme) {
        this.cartUpdateUnsubscriber = window.MinimogEvents.subscribe(
          window.MinimogTheme.pubSubEvents.cartUpdate,
          (event) => {
            if (event?.cart) {
              setTimeout(() => {
                this.requeryElements();
                this.updateNotifications(event.cart);
              }, 50);
            }
          }
        );
      }

      // Listen to cart updated events
      this.cartUpdatedHandler = (event) => {
        if (event.detail?.cart) {
          setTimeout(() => {
            this.requeryElements();
            this.updateNotifications(event.detail.cart);
          }, 50);
        }
      };
      document.addEventListener('cart:updated', this.cartUpdatedHandler);

      // Listen to cart refresh events
      this.cartRefreshHandler = () => {
        setTimeout(() => {
          this.requeryElements();
          this.fetchAndUpdate();
        }, 150);
      };
      document.addEventListener('cart:refresh', this.cartRefreshHandler);

      // Listen to cart drawer open events
      if (window.MinimogEvents && window.MinimogTheme) {
        this.cartDrawerOpenUnsubscriber = window.MinimogEvents.subscribe(
          window.MinimogTheme.pubSubEvents.openCartDrawer,
          () => {
            setTimeout(() => {
              this.requeryElements();
              this.fetchAndUpdate();
            }, 100);
          }
        );
      }

      this.setupComplete = true;
    }

    // Initial update
    this.fetchAndUpdate();
  }

  requeryElements() {
    const elements = document.querySelectorAll('[data-cart-drawer-free-shipping-notification]');
    this.notifications = Array.from(elements);
  }

  async fetchAndUpdate() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.requeryElements();
      this.updateNotifications(cart);
    } catch (error) {
      console.error('Error fetching cart for free shipping notification:', error);
    }
  }

  updateNotifications(cart) {
    if (!cart) return;

    // Always Try requery if empty
    if (this.notifications.length === 0) {
      this.requeryElements();
    }

    const currentTotal = cart.items_subtotal_price || cart.total_price;
    const currentTotalCents = parseInt(currentTotal) || 0;
    const isAchieved = currentTotalCents >= this.freeShippingThreshold;

    this.notifications.forEach(notification => {
      this.updateSingleNotification(notification, currentTotalCents, isAchieved);
    });
  }

  updateSingleNotification(notification, currentTotalCents, isAchieved) {
    if (!notification) return;

    const message = notification.querySelector('[data-cart-drawer-free-shipping-message]');
    const progressFill = notification.querySelector('[data-cart-drawer-free-shipping-progress-fill]');

    const amountNeeded = this.freeShippingThreshold - currentTotalCents;
    const progress = Math.min((currentTotalCents / this.freeShippingThreshold) * 100, 100);

    notification.classList.add('m-cart-drawer__free-shipping-notification--active');
    if (isAchieved) {
      notification.classList.add('m-cart-drawer__free-shipping-notification--achieved');
    } else {
      notification.classList.remove('m-cart-drawer__free-shipping-notification--achieved');
    }

    if (message) {
      if (isAchieved) {
        message.innerHTML = `Congratulations! You've unlocked <strong>FREE Shipping</strong>!`;
      } else {
        const amountFormatted = this.formatMoney(Math.max(0, amountNeeded));
        message.innerHTML = `Buy <strong>${amountFormatted}</strong> more to enjoy <strong>FREE Shipping</strong>`;
      }
    }

    if (progressFill) {
      requestAnimationFrame(() => {
        progressFill.style.width = `${progress}%`;
      });
    }
  }

  formatMoney(cents) {
    if (typeof window.formatMoney === 'function') {
      const moneyFormat = window.MinimogSettings?.money_format || '{{amount}}';
      return window.formatMoney(cents, moneyFormat);
    }

    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.MinimogSettings?.money_format || '{{amount}}');
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.MinimogSettings?.currency_code || 'USD',
    }).format(cents / 100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CartDrawerFreeShipping();
  });
} else {
  new CartDrawerFreeShipping();
}
